import { industryTypeToSlug } from "../constants/industryTypeMap";

export const CM_CERT_FILTER_OPTIONS = [
  { value: "iso_9001_2015", label: "ISO 9001:2015 – Quality Management Systems" },
  { value: "iso_13485_2016", label: "ISO 13485:2016 – Medical Devices" },
  { value: "iatf_16949_2016", label: "IATF 16949:2016 – Automotive Quality Management" },
  { value: "as9100d", label: "AS9100D – Aerospace Quality Management" },
  { value: "iso_14001_2015", label: "ISO 14001:2015 – Environmental Management Systems" },
  { value: "iso_45001_2018", label: "ISO 45001:2018 – Occupational Health & Safety" },
  { value: "iso_22000_2018", label: "ISO 22000:2018 – Food Safety Management" },
  { value: "iso_27001_2013", label: "ISO 27001:2013 – Information Security Management" },
  { value: "iso_50001_2018", label: "ISO 50001:2018 – Energy Management Systems" },
  { value: "iso_22301_2019", label: "ISO 22301:2019 – Business Continuity Management" },
  { value: "iso_20000_1_2018", label: "ISO 20000-1:2018 – IT Service Management" },
  { value: "sa_8000_2014", label: "SA 8000:2014 – Social Accountability" },
  { value: "ce_marking", label: "CE Marking – Conformity for Products in the European Market" },
  { value: "fda_approval", label: "FDA Approval – U.S. Food and Drug Administration Approval" },
  { value: "fcc_certification", label: "FCC Certification – Federal Communications Commission Approval" },
  { value: "cb_scheme", label: "CB Scheme – International Electrotechnical Certification" },
];

export const EMPTY_CM_FILTERS = {
  search: "",
  verifiedOnly: false,
  featuredOnly: false,
  ratingFilter: null,
  selectedIndustryFilters: [],
  selectedStateFilter: "",
  selectedCityFilter: "",
  selectedCmTypeFilter: "",
  selectedFacilityTypeFilter: "",
  selectedCertFilters: [],
};

const looksLikeLocationId = (value) => {
  if (value == null || value === "") return false;
  return /^\d+$/.test(String(value).trim());
};

const getLocation = (item) => {
  const fd = item.facility_details || {};
  const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
  const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
  if (cityLabel || stateLabel) {
    return [cityLabel, stateLabel].filter(Boolean).join(", ");
  }

  const cityRaw = fd.city ?? fd.address?.city;
  const stateRaw = fd.state ?? fd.address?.state;
  const addr = item.address || fd.address;

  if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
    if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(", ");
  }

  if (typeof addr === "string" && addr.trim()) return addr.trim();
  if (addr && typeof addr === "object") {
    const partsNamed = [
      addr.city_name || addr.cityName,
      addr.state_name || addr.stateName,
      addr.country_name || addr.countryName,
    ].filter(Boolean);
    if (partsNamed.length) return partsNamed.join(", ");
  }

  return "";
};

export const getItemStateForFilter = (item) => {
  const fd = item.facility_details || {};
  const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
  if (stateLabel && String(stateLabel).trim()) return String(stateLabel).trim();
  const addr = item.address || fd.address;
  if (addr && typeof addr === "object") {
    const state = addr.state_name || addr.stateName || addr.state;
    if (state && !looksLikeLocationId(state)) return String(state).trim();
  }
  const stateRaw = fd.state ?? fd.address?.state;
  if (stateRaw && !looksLikeLocationId(stateRaw)) return String(stateRaw).trim();
  return "";
};

export const getItemCityForFilter = (item) => {
  const fd = item.facility_details || {};
  const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
  if (cityLabel && String(cityLabel).trim()) return String(cityLabel).trim();
  const addr = item.address || fd.address;
  if (addr && typeof addr === "object") {
    const city = addr.city_name || addr.cityName || addr.city;
    if (city && !looksLikeLocationId(city)) return String(city).trim();
  }
  const cityRaw = fd.city ?? fd.address?.city;
  if (cityRaw && !looksLikeLocationId(cityRaw)) return String(cityRaw).trim();
  return "";
};

export const getItemContractManufacturingType = (item) => {
  const fd = item.facility_details || {};
  const value = fd.contractManufacturingType || fd.contract_manufacturing_type;
  return value != null && String(value).trim() ? String(value).trim() : "";
};

export const getItemFacilityType = (item) => {
  const fd = item.facility_details || {};
  const value = fd.facilityType || fd.facility_type;
  return value != null && String(value).trim() ? String(value).trim() : "";
};

const resolveIndustryOption = (value, industryFilterOptions = []) => {
  if (!value) return null;
  const normalized = String(value).trim();
  const fromApi = industryFilterOptions.find((opt) => opt.value === normalized || opt.label === normalized);
  if (fromApi) return fromApi;
  return { value: normalized, label: normalized };
};

export const getItemIndustryValues = (item, industryFilterOptions = []) => {
  const fd = item.facility_details || {};
  const raw = fd.industryTypes || fd.industry_types || [];
  if (!Array.isArray(raw)) return [];
  const values = new Set();
  raw.forEach((entry) => {
    const value = typeof entry === "string" ? entry : entry?.value ?? entry?.label;
    if (value == null || value === "") return;
    const resolved = resolveIndustryOption(value, industryFilterOptions);
    values.add(resolved ? resolved.value : String(value).trim());
  });
  return [...values];
};

export const getItemIndustrySlugs = (item, industryFilterOptions = []) =>
  getItemIndustryValues(item, industryFilterOptions)
    .map((value) => industryTypeToSlug(value))
    .filter(Boolean);

export const getItemQualityCerts = (item) => {
  const pd = item.production_details || {};
  const raw = pd.qualityCertificationAvailable || pd.quality_certification_available || [];
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((entry) => (typeof entry === "string" ? entry : entry?.value)).filter(Boolean))];
};

export const getManufacturerProductCount = (item) => {
  const pd = item?.product_details || {};
  const products = pd.products;
  return Array.isArray(products) ? products.length : 0;
};

export const getManufacturerRfqCount = (item, rfqCountByCompany = {}) => {
  const companyId = Number(item?.company_id);
  if (!Number.isFinite(companyId) || companyId <= 0) return 0;
  return Number(rfqCountByCompany[companyId]) || 0;
};

export const getRatingDisplay = (item) => {
  const fd = item.facility_details || {};
  const rating = fd.rating ?? fd.average_rating ?? item.rating ?? item.average_rating;
  if (rating == null || rating === "") return null;
  const num = Number(rating);
  return Number.isFinite(num) ? String(num) : String(rating);
};

export const isFeaturedManufacturer = (item) =>
  Boolean(item?.is_featured || item?.featured || item?.facility_details?.featured);

export const hasActiveCmFilters = (filters = EMPTY_CM_FILTERS) =>
  Boolean(
    filters.search?.trim() ||
    filters.verifiedOnly ||
    filters.featuredOnly ||
    filters.ratingFilter != null ||
    filters.selectedIndustryFilters?.length ||
    filters.selectedStateFilter ||
    filters.selectedCityFilter ||
    filters.selectedCmTypeFilter ||
    filters.selectedFacilityTypeFilter ||
    filters.selectedCertFilters?.length,
  );

export const matchesContractManufacturerFilters = (item, filters = EMPTY_CM_FILTERS, industryFilterOptions = []) => {
  if (filters.verifiedOnly && String(item.verification_status || "").toLowerCase() !== "verified") {
    return false;
  }
  if (filters.featuredOnly && !isFeaturedManufacturer(item)) {
    return false;
  }
  if (filters.search?.trim()) {
    const query = filters.search.trim().toLowerCase();
    const name = String(item.company_name || "").toLowerCase();
    const description = String(item.company_description || "").toLowerCase();
    const location = getLocation(item).toLowerCase();
    if (!name.includes(query) && !description.includes(query) && !location.includes(query)) {
      return false;
    }
  }
  if (filters.ratingFilter != null && filters.ratingFilter !== "") {
    const rating = Number(getRatingDisplay(item) || 0);
    if (!Number.isFinite(rating) || rating < Number(filters.ratingFilter)) {
      return false;
    }
  }
  if (filters.selectedIndustryFilters?.length) {
    const itemIndustries = getItemIndustryValues(item, industryFilterOptions);
    const matchesIndustry = filters.selectedIndustryFilters.some((value) => itemIndustries.includes(value));
    if (!matchesIndustry) return false;
  }
  if (filters.selectedStateFilter && getItemStateForFilter(item) !== filters.selectedStateFilter) {
    return false;
  }
  if (filters.selectedCityFilter && getItemCityForFilter(item) !== filters.selectedCityFilter) {
    return false;
  }
  if (filters.selectedCmTypeFilter && getItemContractManufacturingType(item) !== filters.selectedCmTypeFilter) {
    return false;
  }
  if (filters.selectedFacilityTypeFilter && getItemFacilityType(item) !== filters.selectedFacilityTypeFilter) {
    return false;
  }
  if (filters.selectedCertFilters?.length) {
    const certs = getItemQualityCerts(item);
    const matchesCert = filters.selectedCertFilters.some((value) => certs.includes(value));
    if (!matchesCert) return false;
  }
  return true;
};

export const filterContractManufacturers = (list = [], filters = EMPTY_CM_FILTERS, industryFilterOptions = []) =>
  list.filter((item) => matchesContractManufacturerFilters(item, filters, industryFilterOptions));

export const buildContractManufacturerFilterOptions = (list = [], industryFilterOptions = []) => {
  const states = new Set();
  const cities = new Set();
  const cmTypes = new Set();
  const facilityTypes = new Set();
  const industries = new Set();
  const certs = new Set();

  list.forEach((item) => {
    const state = getItemStateForFilter(item);
    const city = getItemCityForFilter(item);
    const cmType = getItemContractManufacturingType(item);
    const facilityType = getItemFacilityType(item);
    if (state) states.add(state);
    if (city) cities.add(city);
    if (cmType) cmTypes.add(cmType);
    if (facilityType) facilityTypes.add(facilityType);
    getItemIndustryValues(item, industryFilterOptions).forEach((value) => industries.add(value));
    getItemQualityCerts(item).forEach((value) => certs.add(value));
  });

  const sidebarIndustryOptions = (industryFilterOptions.length
    ? industryFilterOptions
    : [...industries].map((value) => ({ value, label: value }))
  ).filter((opt) => industries.has(opt.value));

  const knownCerts = CM_CERT_FILTER_OPTIONS.filter((opt) => certs.has(opt.value));
  const knownCertValues = new Set(knownCerts.map((opt) => opt.value));
  const extraCerts = [...certs]
    .filter((value) => !knownCertValues.has(value))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));

  return {
    stateFilterOptions: [...states].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).map((value) => ({ value, label: value })),
    cityFilterOptions: [...cities].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).map((value) => ({ value, label: value })),
    cmTypeFilterOptions: [...cmTypes].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).map((value) => ({ value, label: value })),
    facilityTypeFilterOptions: [...facilityTypes].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).map((value) => ({ value, label: value })),
    sidebarIndustryOptions,
    sidebarCertFilterOptions: [...knownCerts, ...extraCerts],
  };
};

export const mapIndustriesWithManufacturerCounts = (
  industries = [],
  manufacturers = [],
  industryFilterOptions = [],
) => {
  const countsBySlug = {};
  manufacturers.forEach((item) => {
    getItemIndustrySlugs(item, industryFilterOptions).forEach((slug) => {
      countsBySlug[slug] = (countsBySlug[slug] || 0) + 1;
    });
  });

  return industries.map((industry) => ({
    ...industry,
    count: countsBySlug[industry.slug] ?? industry.count ?? 0,
  }));
};

export const mapIndustriesWithManufacturerStats = (
  industries = [],
  manufacturers = [],
  industryFilterOptions = [],
  { useApiCounts = true, rfqCountByCompany = {} } = {},
) => {
  const statsBySlug = {};

  manufacturers.forEach((item) => {
    getItemIndustrySlugs(item, industryFilterOptions).forEach((slug) => {
      if (!statsBySlug[slug]) {
        statsBySlug[slug] = { total: 0, verified: 0, featured: 0, products: 0, rfqs: 0 };
      }
      statsBySlug[slug].total += 1;
      statsBySlug[slug].products += getManufacturerProductCount(item);
      statsBySlug[slug].rfqs += getManufacturerRfqCount(item, rfqCountByCompany);
      if (String(item.verification_status || "").toLowerCase() === "verified") {
        statsBySlug[slug].verified += 1;
      }
      if (isFeaturedManufacturer(item)) {
        statsBySlug[slug].featured += 1;
      }
    });
  });

  return industries.map((industry) => {
    const stats = statsBySlug[industry.slug] || { total: 0, verified: 0, featured: 0, products: 0, rfqs: 0 };
    const manufacturerCount = useApiCounts
      ? Number(industry.count ?? stats.total) || stats.total
      : stats.total;
    const hasManufacturerRows = manufacturers.length > 0;
    const productCount = hasManufacturerRows
      ? stats.products
      : Number(industry.product_count) || 0;
    const rfqCount = hasManufacturerRows
      ? stats.rfqs
      : Number(industry.rfq_count) || 0;
    const verifiedCount = hasManufacturerRows
      ? stats.verified
      : Number(industry.verified_count) || 0;
    const featuredCount = hasManufacturerRows
      ? stats.featured
      : Number(industry.featured_count) || 0;

    return {
      ...industry,
      count: manufacturerCount,
      supplier_count: manufacturerCount,
      product_count: productCount,
      rfq_count: rfqCount,
      verified_count: verifiedCount,
      featured_count: featuredCount,
      buyer_request_count: verifiedCount,
    };
  });
};
